/**
 * hc-plp-color-swatches.js
 * ---------------------------------------------------------------------------
 * Shared logic for the PLP / collection card colorway swatch row.
 *
 * This file is loaded ONCE per page (see snippets/hc-plp-swatches-global.liquid)
 * — not once per product card. It reads its Shopify-specific config
 * (storefront token, shop domain, CDN path, fallback colors) from
 * window.hcPlpSwatchConfig, which that snippet sets before loading this file.
 *
 * Each product card only renders a small skeleton div
 * (snippets/hc-plp-color-swatches.liquid); this script finds all of them via
 * [data-plp-swatch-group] and fills them in.
 */
(function () {
  if (window.__hcPlpSwatchesInit) {
    if (window.hcPlpSwatches) window.hcPlpSwatches.init();
    return;
  }
  window.__hcPlpSwatchesInit = true;

  var config = window.hcPlpSwatchConfig || {};
  var STOREFRONT_TOKEN = config.storefrontToken;
  var SHOP_DOMAIN = config.shopDomain;
  var CDN_FILES_PATH = config.cdnFilesPath;
  var SIZES = config.sizes || { desktop: 34, mobile: 26, gapDesktop: 10, gapMobile: 18, breakpoint: 749 };
  var MAX_VISIBLE = 5;
  // Same cache key format as hc-color-swatches.liquid (PDP) so navigating
  // PLP -> PDP -> PLP doesn't re-fetch the same style_group twice.
  var CACHE_PREFIX = 'swatches-';

  var FALLBACK_HEX = (function () {
    var raw = config.fallbackHexRaw || '';
    var map = { multi: null };
    raw.split('\n').forEach(function (line) {
      var parts = line.split(':').map(function (s) { return s.trim(); });
      if (parts[0] && parts[1]) map[parts[0].toLowerCase()] = parts[1];
    });
    return map;
  })();

  function buildCdnUrl(colorName, seasonCode, colorPattern) {
    var slug = colorName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    var patternSlug = colorPattern
      ? colorPattern.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : '';
    var suffix = patternSlug ? '-' + patternSlug : '';
    return CDN_FILES_PATH + 'swatch-' + seasonCode + '-' + slug + suffix + '.webp';
  }

  function fetchSiblings(styleGroup) {
    var cacheKey = CACHE_PREFIX + styleGroup;
    var cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      var parsed = JSON.parse(cached);
      // Cache written by the PDP swatches (or an older version of this file)
      // may not include featuredImage/images — refetch in that case so the
      // PLP card's image swap (primary + hover/secondary) has data to use.
      if (parsed.length > 0 && parsed[0].featuredImage !== undefined && parsed[0].images !== undefined) {
        return Promise.resolve(parsed);
      }
      if (parsed.length === 0) sessionStorage.removeItem(cacheKey);
    }

    var safe = styleGroup.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

    var query = '{' +
      'products(first: 20, query: "metafield:custom.style_group:' + safe + '") {' +
      'nodes {' +
      'id handle onlineStoreUrl ' +
      'featuredImage { url altText }' +
      'images(first: 2) { nodes { url altText } }' +
      'metafields(identifiers: [' +
      '{namespace: "custom", key: "style_group"},' +
      '{namespace: "custom", key: "color_name"},' +
      '{namespace: "custom", key: "season_code"},' +
      '{namespace: "custom", key: "color_family"},' +
      '{namespace: "custom", key: "color_pattern"}' +
      ']) { key value }' +
      '}' +
      '}' +
      '}';

    return fetch('https://' + SHOP_DOMAIN + '/api/2024-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query: query }),
    })
      .then(function (res) { return res.json(); })
      .then(function (json) {
        if (json.errors) console.error('[hc-plp-swatches] GraphQL errors:', json.errors);

        var siblings = ((json.data && json.data.products && json.data.products.nodes) || []).filter(function (node) {
          var sgField = node.metafields.filter(Boolean).find(function (m) { return m.key === 'style_group'; });
          return sgField && sgField.value === styleGroup;
        });

        if (siblings.length > 0) sessionStorage.setItem(cacheKey, JSON.stringify(siblings));
        return siblings;
      });
  }

  function metaMap(sibling) {
    var map = {};
    sibling.metafields.filter(Boolean).forEach(function (m) { map[m.key] = m.value; });
    return map;
  }

  // NOTE: .hc-plp-swatches renders as a SIBLING of .grid-product__content
  // (both children of .grid__item), not a descendant of it — confirmed
  // against the live rendered DOM. Scope from .grid__item and query back
  // down into .grid-product__content, not the other way around.
  function getContentRoot(group) {
    return group.closest('.grid__item');
  }

  function getImageWrapper(group) {
    var root = getContentRoot(group);
    return root ? root.querySelector('.grid-product__content [data-product-image-wrapper] img') : null;
  }

  // The secondary/hover image is the theme's existing "show a second photo
  // on hover" feature (.grid-product__secondary-image) — a different <img>
  // than the primary one, sitting alongside it inside .grid-product__image-mask.
  // It needs to be kept in sync too, or hovering the card after picking a
  // different colorway shows the ORIGINAL colorway's second photo.
  function getSecondaryImageWrapper(group) {
    var root = getContentRoot(group);
    return root ? root.querySelector('.grid-product__content .grid-product__secondary-image img') : null;
  }

  function getProductLink(group) {
    var root = getContentRoot(group);
    return root ? root.querySelector('.grid-product__content [data-product-link]') : null;
  }

  function secondaryImageUrl(sibling) {
    var nodes = sibling.images && sibling.images.nodes;
    // images.nodes[0] is usually the same as featuredImage; the hover slot
    // wants whatever the theme would show as the second photo.
    var second = nodes && (nodes[1] || nodes[0]);
    return second && second.url;
  }

  // Commits a selection: swaps the card image (+ hover/secondary image), moves the link, persists as the new "default".
  function applySelection(group, sibling, mf) {
    var img = getImageWrapper(group);
    var secondaryImg = getSecondaryImageWrapper(group);
    var link = getProductLink(group);

    if (img && sibling.featuredImage && sibling.featuredImage.url) {
      img.dataset.originalSrc = sibling.featuredImage.url;
      img.dataset.originalSrcset = '';
      img.src = sibling.featuredImage.url;
      img.removeAttribute('srcset');
      img.alt = sibling.featuredImage.altText || mf.color_name || img.alt;
    }
    var secondaryUrl = secondaryImageUrl(sibling);
    if (secondaryImg && secondaryUrl) {
      secondaryImg.dataset.originalSrc = secondaryUrl;
      secondaryImg.dataset.originalSrcset = '';
      secondaryImg.src = secondaryUrl;
      secondaryImg.removeAttribute('srcset');
    }
    if (link) {
      link.setAttribute('href', sibling.onlineStoreUrl || '/products/' + sibling.handle);
    }
  }

  // Hover-only: swap the image(s) without touching the link/selection state.
  function previewImage(group, sibling) {
    var img = getImageWrapper(group);
    var secondaryImg = getSecondaryImageWrapper(group);
    var secondaryUrl = secondaryImageUrl(sibling);

    if (img && sibling.featuredImage && sibling.featuredImage.url) {
      if (img.dataset.originalSrc === undefined) {
        img.dataset.originalSrc = img.getAttribute('src') || '';
        img.dataset.originalSrcset = img.getAttribute('srcset') || '';
      }
      img.src = sibling.featuredImage.url;
      img.removeAttribute('srcset');
    }
    if (secondaryImg && secondaryUrl) {
      if (secondaryImg.dataset.originalSrc === undefined) {
        secondaryImg.dataset.originalSrc = secondaryImg.getAttribute('src') || '';
        secondaryImg.dataset.originalSrcset = secondaryImg.getAttribute('srcset') || '';
      }
      secondaryImg.src = secondaryUrl;
      secondaryImg.removeAttribute('srcset');
    }
  }

  function restoreSelectedImage(group) {
    [getImageWrapper(group), getSecondaryImageWrapper(group)].forEach(function (img) {
      if (!img) return;
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        if (img.dataset.originalSrcset) img.setAttribute('srcset', img.dataset.originalSrcset);
      }
    });
  }

  function computeVisibleCount(group) {
    var isMobile = window.matchMedia('(max-width: ' + SIZES.breakpoint + 'px)').matches;
    var circleSize = isMobile ? SIZES.mobile : SIZES.desktop;
    var gap = isMobile ? SIZES.gapMobile : SIZES.gapDesktop;
    var moreLabelWidth = 28;

    var rect = group.getBoundingClientRect();
    var containerWidth = rect.width || (group.parentElement ? group.parentElement.getBoundingClientRect().width : 0);
    if (!containerWidth) return MAX_VISIBLE;

    var perSwatch = circleSize + gap;
    var fit = Math.floor((containerWidth - moreLabelWidth) / perSwatch);
    return Math.max(1, Math.min(MAX_VISIBLE, fit));
  }

  // Mirrors the exact markup hc-color-swatches.liquid renders per swatch on
  // PDP (.variant-input > input[type=radio][hidden] + label.variant__button-label.color-swatch),
  // so this inherits the theme's real .color-swatch / [aria-current="true"]
  // styling instead of a parallel invented look.
  function renderSwatchInput(opts) {
    var sibling = opts.sibling, mf = opts.mf, isSelected = opts.isSelected, cdnUrl = opts.cdnUrl;

    var wrap = document.createElement('div');
    wrap.className = 'variant-input';
    wrap.dataset.value = mf.color_name || '';

    var input = document.createElement('input');
    input.type = 'radio';
    input.name = 'Color';
    input.value = mf.color_name || '';
    input.dataset.colorName = mf.color_name || '';
    if (isSelected) input.checked = true;
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    input.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:0;height:0;';

    var label = document.createElement('label');
    label.className = 'variant__button-label color-swatch';
    label.setAttribute('aria-label', mf.color_name || '');
    label.setAttribute('title', mf.color_name || '');
    label.setAttribute('aria-current', isSelected ? 'true' : 'false');
    if (mf.color_family) label.dataset.colorFamily = mf.color_family.toLowerCase();
    label.style.setProperty('--element-swatch-color-background-image', "url('" + cdnUrl + "')");
    label.style.cursor = 'pointer';

    var fallbackHex = FALLBACK_HEX[(mf.color_family || '').toLowerCase()] || '#CCCCCC';
    var img = document.createElement('img');
    img.src = cdnUrl;
    img.alt = mf.color_name || '';
    img.loading = 'lazy';
    img.width = 40;
    img.height = 40;
    img.style.cssText = 'display:block;width:100%;height:100%;object-fit:cover;border-radius:inherit;';
    img.onerror = function () {
      label.style.removeProperty('--element-swatch-color-background-image');
      label.style.backgroundColor = fallbackHex;
      img.remove();
    };

    label.appendChild(img);
    wrap.appendChild(input);
    wrap.appendChild(label);
    return { wrap: wrap, input: input, label: label };
  }

  function processGroup(group) {
    if (group.dataset.plpSwatchesProcessed === 'true') return Promise.resolve();
    group.dataset.plpSwatchesProcessed = 'true';

    var styleGroup = group.dataset.styleGroup;
    var currentGid = 'gid://shopify/Product/' + group.dataset.currentProductId;

    return fetchSiblings(styleGroup)
      .then(function (siblings) {
        // fetchSiblings' style_group search includes the CURRENT product
        // itself (its own metafield matches too), so a true single-colorway
        // style comes back with siblings.length === 1 (just itself), not 0.
        // <= 1 -> no real siblings -> no swatch row at all.
        if (siblings.length <= 1) {
          group.remove();
          return;
        }

        var ordered = siblings.filter(function (s) { return s.id === currentGid; })
          .concat(siblings.filter(function (s) { return s.id !== currentGid; }));

        var visibleCount = computeVisibleCount(group);
        var visible = ordered.slice(0, visibleCount);
        var remainder = ordered.length - visible.length;

        group.innerHTML = '';

        visible.forEach(function (sibling) {
          var mf = metaMap(sibling);
          var isSelected = sibling.id === currentGid;
          var cdnUrl = buildCdnUrl(mf.color_name || '', mf.season_code || '', mf.color_pattern || '');
          var parts = renderSwatchInput({ sibling: sibling, mf: mf, isSelected: isSelected, cdnUrl: cdnUrl });
          var wrap = parts.wrap, input = parts.input, label = parts.label;

          label.addEventListener('click', function (e) {
            e.preventDefault(); // this label isn't form-connected; we drive selection state ourselves
            group.querySelectorAll('.color-swatch').forEach(function (l) { l.setAttribute('aria-current', 'false'); });
            label.setAttribute('aria-current', 'true');
            group.querySelectorAll('input[type="radio"]').forEach(function (i) { i.checked = false; });
            input.checked = true;
            applySelection(group, sibling, mf);
          });

          // Desktop-only hover preview; click always commits regardless of device.
          label.addEventListener('mouseenter', function () {
            if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
            if (label.getAttribute('aria-current') === 'true') return;
            previewImage(group, sibling);
          });
          label.addEventListener('mouseleave', function () {
            if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
            if (label.getAttribute('aria-current') === 'true') return;
            restoreSelectedImage(group);
          });

          group.appendChild(wrap);
        });

        if (remainder > 0) {
          var more = document.createElement('span');
          more.className = 'hc-plp-swatch-more';
          more.textContent = '+' + remainder;
          more.setAttribute('aria-hidden', 'true');
          group.appendChild(more);
        }
      })
      .catch(function (err) {
        console.error('[hc-plp-swatches] error:', err);
        group.dataset.plpSwatchesProcessed = 'false';
      });
  }

  function init(root) {
    var scope = root || document;
    var groups = scope.querySelectorAll('[data-plp-swatch-group]:not([data-plp-swatches-processed="true"])');
    return groups.length ? Promise.all(Array.prototype.map.call(groups, processGroup)) : Promise.resolve();
  }

  window.hcPlpSwatches = { init: init, processGroup: processGroup };

  function startObserver() {
    init();
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var isGroup = node.matches && node.matches('[data-plp-swatch-group]');
          var hasGroup = node.querySelector && node.querySelector('[data-plp-swatch-group]:not([data-plp-swatches-processed="true"])');
          if (isGroup || hasGroup) init(node.parentNode || document);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) {
    startObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startObserver);
  }
})();